import React, { useState } from "react";
import ContentEditable from "react-contenteditable";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { stripHtmlUsingDOMParser } from "../../../utils/helpers";

import { toast } from "react-toastify";
import { useUnlockWheelV2 } from "../../../services/mutations";
import WheelCardThreeDotMenu, {
  menuItemKeys,
} from "../../../components/WheelCardThreeDotMenu/WheelCardThreeDotMenu";
import AccessKeyEnterModal from "../../../components/AccessKeyEnterModal";
import { ASSETS } from "../../../utils/config";
import { TextView } from "../../../components";
import WheelComponent from "../../../components/WheelComponent";
import styled from "styled-components";
import { colors } from "../../../utils";
import { Stack } from "@mui/material";
import UserImage from "../../../components/UserImage";
import CloningDisabled from "../../../assets/icons/CloningDisabled";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AmbassadorIcon from "../../../assets/icons/AmbassadorIcon";
import FavoriteHeartIcon from "../../../components/FavoriteHeartIcon/FavoriteHeartIcon";
import IyugaTooltip from "../../../components/IyugaTooltip";
import CloneGreenIcon from "../../../assets/images/clone_green.svg";

const CardImageContainer = styled("div")(({ bg }) => {
  let background = "#F6F7F9";
  const isImageUrl = (bg) => /^(http|https|data):/.test(bg);

  if (isImageUrl(bg)) {
    background = `url(${bg}) center/cover no-repeat`;
  } else if (bg) {
    background = bg;
  }

  return {
    width: "100%",
    background,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    flexGrow: 1,
    overflow: "hidden",
    position: "relative",
  };
});

const WheelCard = ({ data = {}, showUser = true }) => {
  const [openPasswordModal, setopenPasswordModal] = useState(false);
  const nav = useNavigate();
  const shouldShowPasswordModal = data?.is_locked && !data?.is_unlocked;
  const wheelSegments = Array.isArray(data?.wheel_segments)
    ? data.wheel_segments
    : [];
  const userProfile = data?.user_profile;

  const wheelUpdatedData = [...wheelSegments]?.map((obj) => ({
    ...obj,
  }));

  const wheelUpdatedDataSorted = wheelUpdatedData.sort(
    (a, b) => a.position - b.position
  );

  const getWheelBackground = (wallpaperSettings = {}) => {
    const { bg_wallpaper, current_bg, segment_bg_color } = wallpaperSettings;
    if (current_bg === 2 || current_bg === 3) {
      return bg_wallpaper;
    }
    return segment_bg_color;
  };

  const handleOpenPreview = () => {
    if (shouldShowPasswordModal) {
      setopenPasswordModal(true);
      return;
    }
    nav(`/wheel/${data?.id}`);
  };

  const unlockWheel = useUnlockWheelV2();

  const handleUnlock = (access_code) => {
    const wheelId = data?.id;

    if (access_code.length === 0) {
      toast.error("Access code cannot be empty");
      return;
    }

    unlockWheel.mutate(
      { wheelId: wheelId, access_code, lock_type: 2 },
      {
        onSuccess: () => {
          toast.success("Wheel Unlocked", false);

          nav(
            `/wheel/${wheelId}`
          );
          setopenPasswordModal(false);
          // queryClient.invalidateQueries("wheels"); // Adjust this key based on your cache structure
        },
        onError: () => {
          toast.error("Incorrect Access Code", true);
        },
      }
    );
  };

  const MenuItems = [
    menuItemKeys.CLONE,
    menuItemKeys.SHARE,
    menuItemKeys.REPORT,
  ];

  const RenderIcons = ({ direction = "row", iconBackground = "#F1F1F1" }) => {
    return (
      <Stack direction={direction} alignItems="center" gap={1}>
        {!data?.is_clonable && (
          <IyugaTooltip tooltip="Cloning Disabled">
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: iconBackground,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloningDisabled width={14} height={14} fill={colors.darkGrey} />
            </div>
          </IyugaTooltip>
        )}
        {data?.is_locked && (
          <IyugaTooltip tooltip="Locked Wheel">
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: iconBackground,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockIcon
                style={{
                  color: colors.darkGrey,
                  fontSize: "16px",
                  width: 16,
                  height: 16,
                }}
              ></LockIcon>
            </div>
          </IyugaTooltip>
        )}
      </Stack>
    );
  };

  const Header = () => {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          onClick={() => {
            nav(
              userProfile?.user_id ? `/gallery/${userProfile?.user_id}` : "/"
            );
          }}
        >
          <UserImage image={userProfile?.profile_image} size={28} />
          <TextView
            style={{ fontSize: "15px", color: colors.darkGrey }}
            variant="subtitle"
          >
            {userProfile?.first_name} {userProfile?.last_name}
          </TextView>
          {/* <AmbassadorIcon size={14} /> */}
        </Stack>
        <RenderIcons direction="row" />
      </Stack>
    );
  };

  const Footer = () => {
    return (
      <Stack gap={1}>
        <ContentEditable
          style={{
            cursor: "pointer",
            WebkitLineClamp: 2,
            fontWeight: "600",
            fontSize: "16px",
            color: "#123051",
            padding: 0,
            fontFamily: "DM Sans",
            lineHeight: "20px",
            marginBottom: "0px",
            margin: 0,
          }}
          html={stripHtmlUsingDOMParser(data?.wheel_name)}
          disabled
          onClick={handleOpenPreview}
        />
        <Stack
          justifyContent={"space-between"}
          direction="row"
          alignItems="center"
          gap={1}
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IyugaTooltip tooltip="Times Viewed">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <VisibilityIcon
                  style={{ fontSize: "16px", color: "#B3B3B3" }}
                />
                <TextView
                  variant="subtitle"
                  style={{ fontSize: "12px", color: colors.textBlue }}
                >
                  {data?.no_of_views || 0}
                </TextView>
              </Stack>
            </IyugaTooltip>
            <IyugaTooltip tooltip="Times Favorited">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <FavoriteIcon
                  style={{ fontSize: "16px", color: colors.error }}
                />
                <TextView
                  variant="subtitle"
                  style={{ fontSize: "12px", color: colors.textBlue }}
                >
                  {data?.num_favorites || 0}
                </TextView>
              </Stack>
            </IyugaTooltip>
            <IyugaTooltip tooltip="Times Cloned">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <img
                  src={CloneGreenIcon}
                  style={{ width: "16px", height: "16px" }}
                  alt="Cloned"
                />
                <TextView
                  variant="subtitle"
                  style={{ fontSize: "12px", color: colors.textBlue }}
                >
                  {data?.num_clones || 0}
                </TextView>
              </Stack>
            </IyugaTooltip>
          </Stack>
          <Stack
            justifyContent={"space-between"}
            direction="row"
            alignItems="center"
            gap={0.5}
          >
            <IyugaTooltip tooltip="Favorite">
              <FavoriteHeartIcon
                wheelId={data?.id}
                isFavorited={data?.is_favourite}
                size="18px"
              />
            </IyugaTooltip>
            <WheelCardThreeDotMenu data={data} include={MenuItems} />
          </Stack>
        </Stack>
      </Stack>
    );
  };
  return (
    <>
      <AccessKeyEnterModal
        onSubmit={(password) => {
          handleUnlock(password);
        }}
        handleClose={() => {
          setopenPasswordModal(false);
        }}
        isOpen={openPasswordModal}
      />
      <Stack
        gap={1.2}
        style={{
          borderRadius: 8,
          padding: "14px",
          border: `1px solid ${colors.lightBorder}`,
          cursor: "pointer",
          height: "100%",
        }}
      >
        {showUser && <Header></Header>}
        <CardImageContainer
          bg={
            data?.version === 1
              ? data?.wallpaper
              : getWheelBackground(data?.wallpaper_settings)
          }
          onClick={handleOpenPreview}
        >
          {!showUser && (
            <div
              style={{ position: "absolute", top: 10, right: 10, zIndex: 100 }}
            >
              <RenderIcons iconBackground="#FFFFFF" direction="column" />
            </div>
          )}
          <WheelComponent
            userData={{
              id: uuidv4(),
              url: data?.wheel_image || ASSETS.userPlaceholder,
            }}
            maxSelectedArcs={0}
            arcThickness={-10}
            chartData={wheelUpdatedDataSorted || []}
            margin={25}
            setChartData={() => {}}
            containerDivStyle={{
              width: "23vh",
              height: "23vh",
            }}
          />
        </CardImageContainer>
        <Footer />
      </Stack>
    </>
  );
};

export default WheelCard;
